
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { EstimateStratigraphy } from '@/types/estimateStratigraphy';

function safeNumber(val: any, fallback = 0) {
  const n = Number(val);
  return !isFinite(n) || isNaN(n) ? fallback : n;
}

function safeInt(val: any, fallback = 1) {
  const n = parseInt(val);
  return !isFinite(n) || isNaN(n) ? fallback : n;
}

export const useEstimateStratigraphiesQuery = (estimateId?: string) => {
  const { user } = useAuth();

  const { data: estimateStratigraphies = [], isLoading, error } = useQuery({
    queryKey: ['estimate-stratigraphies', estimateId],
    queryFn: async () => {
      if (!user || !estimateId) return [];
      
      console.log('Fetching estimate stratigraphies for estimate:', estimateId);

      const { data, error } = await supabase
        .from('estimate_stratigraphies')
        .select(`
          *,
          perimeter_profile:materials!estimate_stratigraphies_perimeter_profile_id_fkey (
            id, name, code, ean_code, unit, unit_price, supplier, supplier_id,
            category, color_hex, width, sheet_thickness, incidence_per_sqm,
            installation_time_per_sqm, box_pieces, waste_percentage
          ),
          stratigraphies: stratigraphies!estimate_stratigraphies_stratigraphy_id_fkey (
            id,
            name,
            description,
            type,
            total_thickness,
            cost_per_sqm,
            material_cost_per_sqm,
            screw_cost_per_sqm,
            labor_cost_per_sqm,
            installation_time_per_sqm,
            is_certified,
            fire_resistance_class,
            acoustic_performance,
            weight_per_sqm,
            thermal_performance,
            layers: layers (
              id,
              material_id,
              thickness,
              position,
              inter_axis,
              screw_material_id,
              screw_quantity,
              screw_cost_per_sqm,
              material_cost_per_sqm,
              installation_time_minutes,
              material_quantity_per_sqm,
              anchor_material_id,
              materials!layers_material_id_fkey (
                id,
                name,
                code,
                ean_code,
                color_hex,
                category,
                unit,
                unit_price,
                supplier,
                supplier_id,
                incidence_per_sqm,
                weight_per_sqm,
                thermal_conductivity,
                acoustic_performance,
                fire_resistance_class,
                compatible_board_types,
                width,
                length,
                box_pieces,
                installation_time_per_sqm,
                waste_percentage,
                disposal_percentage,
                material_type,
                board_typology,
                en_520_type
              ),
              screw_materials:materials!layers_screw_material_id_fkey (
                id,
                name,
                code,
                ean_code,
                color_hex,
                category,
                unit,
                unit_price,
                supplier,
                supplier_id,
                incidence_per_sqm,
                length,
                box_pieces,
                installation_time_per_sqm,
                waste_percentage
              ),
              anchor_materials:materials!layers_anchor_material_id_fkey (
                id,
                name,
                code,
                ean_code,
                category,
                unit,
                unit_price,
                supplier,
                supplier_id,
                box_pieces,
                installation_time_per_sqm,
                waste_percentage
              )
            )
          )
        `)
        .eq('estimate_id', estimateId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching estimate stratigraphies:', error);
        throw error;
      }

      const rows = (data || []).map(item => {
        // Notare che area e quantity vanno normalizzati!
        const area = safeNumber(item.area, 0);
        const quantity = safeInt(item.quantity, 1);
        const unitCost = safeNumber(item.unit_cost, 0);
        const totalCost = safeNumber(item.total_cost, 0);

        if (item.is_snapshot && item.stratigraphy_data) {
          const stratigraphyData = (typeof item.stratigraphy_data === 'object' && item.stratigraphy_data !== null)
            ? item.stratigraphy_data
            : {};
          
          const snapshotName = typeof stratigraphyData['name'] === 'string'
            ? stratigraphyData['name']
            : '';
          
          const snapshotDescription = typeof stratigraphyData['description'] === 'string'
            ? stratigraphyData['description']
            : '';

          const stratigraphy = {
            ...(typeof stratigraphyData === 'object' && stratigraphyData !== null ? stratigraphyData : {}),
            layers: Array.isArray(item.layers_data) ? item.layers_data : [],
          };

          return {
            id: item.id,
            estimateId: item.estimate_id,
            stratigraphyId: item.stratigraphy_id,
            name: item.name ?? snapshotName,
            description: item.description ?? snapshotDescription,
            area,
            quantity,
            unitCost,
            totalCost,
            createdAt: new Date(item.created_at),
            updatedAt: new Date(item.updated_at),
            stratigraphyData: item.stratigraphy_data,
            layersData: item.layers_data,
            pricesUpdatedAt: item.prices_updated_at ? new Date(item.prices_updated_at) : undefined,
            // F32: altezza parete dichiarata. Per le righe pre-F32 puo` essere
            // NULL → useMaterialsSummary applica fallback 2.7m.
            wallHeight: (item as any).wall_height != null ? Number((item as any).wall_height) : undefined,
            isSnapshot: true,
            originalStratigraphyId: item.original_stratigraphy_id,
            finishLevel: (item as any).finish_level ?? null,
            finishCostPerSqm: (item as any).finish_cost_per_sqm != null ? Number((item as any).finish_cost_per_sqm) : null,
            finishLaborMinutesPerSqm: (item as any).finish_labor_minutes_per_sqm != null ? Number((item as any).finish_labor_minutes_per_sqm) : null,
            finishComponentsData: Array.isArray((item as any).finish_components_data) ? (item as any).finish_components_data : null,
            vatRate: (item as any).vat_rate != null ? Number((item as any).vat_rate) : null,
            markupPct: (item as any).markup_pct != null ? Number((item as any).markup_pct) : null,
            difficultyMultiplier: (item as any).difficulty_multiplier != null ? Number((item as any).difficulty_multiplier) : 1,
            // F17.2.14: campi ceiling-specific (null per le pareti)
            perimeterM: (item as any).perimeter_m != null ? Number((item as any).perimeter_m) : null,
            perimeterProfileCost: (item as any).perimeter_profile_cost != null ? Number((item as any).perimeter_profile_cost) : null,
            perimeterProfileId: (item as any).perimeter_profile_id ?? null,
            // F17 post-2026-05-22: snapshot del material profilo perimetrale (join FK)
            perimeterProfile: (item as any).perimeter_profile ?? null,
            stratigraphy,
          };
        }

        if (item.stratigraphies && typeof item.stratigraphies === 'object' && !Array.isArray(item.stratigraphies)) {
          return {
            id: item.id,
            estimateId: item.estimate_id,
            stratigraphyId: item.stratigraphy_id,
            name: item.name,
            description: item.description || '',
            area,
            quantity,
            unitCost,
            totalCost,
            createdAt: new Date(item.created_at),
            updatedAt: new Date(item.updated_at),
            stratigraphyData: item.stratigraphy_data,
            layersData: item.layers_data,
            pricesUpdatedAt: item.prices_updated_at ? new Date(item.prices_updated_at) : undefined,
            // F32: altezza parete dichiarata. Per le righe pre-F32 puo` essere
            // NULL → useMaterialsSummary applica fallback 2.7m.
            wallHeight: (item as any).wall_height != null ? Number((item as any).wall_height) : undefined,
            isSnapshot: item.is_snapshot || false,
            originalStratigraphyId: item.original_stratigraphy_id,
            finishLevel: (item as any).finish_level ?? null,
            finishCostPerSqm: (item as any).finish_cost_per_sqm != null ? Number((item as any).finish_cost_per_sqm) : null,
            finishLaborMinutesPerSqm: (item as any).finish_labor_minutes_per_sqm != null ? Number((item as any).finish_labor_minutes_per_sqm) : null,
            finishComponentsData: Array.isArray((item as any).finish_components_data) ? (item as any).finish_components_data : null,
            vatRate: (item as any).vat_rate != null ? Number((item as any).vat_rate) : null,
            markupPct: (item as any).markup_pct != null ? Number((item as any).markup_pct) : null,
            difficultyMultiplier: (item as any).difficulty_multiplier != null ? Number((item as any).difficulty_multiplier) : 1,
            // F17.2.14: campi ceiling-specific (null per le pareti)
            perimeterM: (item as any).perimeter_m != null ? Number((item as any).perimeter_m) : null,
            perimeterProfileCost: (item as any).perimeter_profile_cost != null ? Number((item as any).perimeter_profile_cost) : null,
            perimeterProfileId: (item as any).perimeter_profile_id ?? null,
            // F17 post-2026-05-22: snapshot del material profilo perimetrale (join FK)
            perimeterProfile: (item as any).perimeter_profile ?? null,
            stratigraphy: {
              ...item.stratigraphies,
              layers: item.stratigraphies.layers?.sort((a: any, b: any) => a.position - b.position) || [],
            },
          };
        }

        // Orphan case, no snapshot and no stratigrafia original
        return {
          id: item.id,
          estimateId: item.estimate_id,
          stratigraphyId: item.stratigraphy_id,
          name: item.name || 'Stratigrafia eliminata',
          description: item.description || 'La stratigrafia originale è stata eliminata',
          area,
          quantity,
          unitCost,
          totalCost,
          createdAt: new Date(item.created_at),
          updatedAt: new Date(item.updated_at),
          stratigraphyData: item.stratigraphy_data,
          layersData: item.layers_data,
          pricesUpdatedAt: item.prices_updated_at ? new Date(item.prices_updated_at) : undefined,
          isSnapshot: item.is_snapshot || false,
          originalStratigraphyId: item.original_stratigraphy_id,
          finishLevel: (item as any).finish_level ?? null,
          finishCostPerSqm: (item as any).finish_cost_per_sqm != null ? Number((item as any).finish_cost_per_sqm) : null,
          finishLaborMinutesPerSqm: (item as any).finish_labor_minutes_per_sqm != null ? Number((item as any).finish_labor_minutes_per_sqm) : null,
          finishComponentsData: Array.isArray((item as any).finish_components_data) ? (item as any).finish_components_data : null,
          difficultyMultiplier: (item as any).difficulty_multiplier != null ? Number((item as any).difficulty_multiplier) : 1,
          // F17.2.14: campi ceiling-specific (null per le pareti)
          perimeterM: (item as any).perimeter_m != null ? Number((item as any).perimeter_m) : null,
          perimeterProfileCost: (item as any).perimeter_profile_cost != null ? Number((item as any).perimeter_profile_cost) : null,
          perimeterProfileId: (item as any).perimeter_profile_id ?? null,
          perimeterProfile: (item as any).perimeter_profile ?? null,
          stratigraphy: null,
        };
      }) as (EstimateStratigraphy & { stratigraphy: any, quantity: number })[];

      // 🔥 Prezzi NETTI dell'org corrente sui layer, DOPO il mapping.
      //
      // Va fatto qui e non sul risultato grezzo della query perche' `stratigraphy`
      // ha due provenienze: le righe normali usano il join FK su `materials`
      // (dove `unit_price` e' il valore denormalizzato, quindi NON il netto
      // dell'org: lo scriveva il trigger di ricalcolo senza filtrare i
      // materiali per organizzazione), le righe "Copia indipendente" usano
      // invece lo snapshot `layers_data`. Patchando solo il primo ramo gli
      // snapshot — che sono il caso piu' comune — restavano fuori.
      //
      // La view `materials_with_pricing` risolve la precedenza corretta
      // (prezzo custom org > listino × catena famiglia × extra) ed e' scopata
      // su `current_org_id()`. Stessa tecnica di
      // useBulkUpdateEstimateStratigraphyPrices (F19.4).
      //
      // Serve al Riepilogo Materiali da Acquistare, che legge questi
      // `unit_price` per costruire la lista d'ordine.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const allLayers: any[] = [];
      for (const row of rows) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const l of ((row as any).stratigraphy?.layers ?? [])) allLayers.push(l);
      }
      const matIds = new Set<string>();
      for (const l of allLayers) {
        const mat = l?.materials ?? l?.material;
        const screw = l?.screw_materials ?? l?.screwMaterial;
        if (mat?.id) matIds.add(mat.id);
        if (screw?.id) matIds.add(screw.id);
      }
      if (matIds.size > 0) {
        const { data: pricing, error: pricingError } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from('materials_with_pricing' as any)
          // `length` serve solo all'avviso "serve un giunto orizzontale" nel
          // Riepilogo: lo snapshot `layers_data` non lo salva, quindi sulle
          // righe "Copia indipendente" quel badge non compariva mai. Non
          // influenza le quantita' da ordinare (BOARD_ORDER_MODEL = 'reuse' in
          // useMaterialsSummary ignora la geometria del foglio).
          .select('id, unit_price, length')
          .in('id', Array.from(matIds));
        if (pricingError) {
          // Non blocchiamo la pagina: meglio i prezzi dello snapshot che nessun
          // dato, ma va segnalato perche' i totali RDA risulterebbero sfalsati.
          console.warn('[useEstimateStratigraphiesQuery] prezzi netti non caricati:', pricingError.message);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const catalog = new Map<string, any>();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          for (const p of (pricing ?? []) as any[]) catalog.set(p.id, p);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const applyTo = (mat: any) => {
            const fresh = mat?.id ? catalog.get(mat.id) : undefined;
            if (!fresh) return;
            mat.unit_price = Number(fresh.unit_price ?? 0);
            // Solo se assente: non riscriviamo cio' che lo snapshot congela.
            if (mat.length == null && fresh.length != null) mat.length = fresh.length;
          };
          for (const l of allLayers) {
            applyTo(l?.materials ?? l?.material);
            applyTo(l?.screw_materials ?? l?.screwMaterial);
          }
        }
      }

      return rows;
    },
    enabled: !!user && !!estimateId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const totalCost = estimateStratigraphies.reduce((sum, item) => sum + (typeof item.totalCost === "number" && !isNaN(item.totalCost) ? item.totalCost : 0), 0);

  return {
    estimateStratigraphies,
    isLoading,
    error,
    totalCost,
  };
};
